import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Modal } from "../components/Modal";

import {
  BETA_STATUS_LABEL as STATUS_LABEL,
  betaCsvFileName,
  buildBetaCsv,
  downloadCsv,
} from "./admin/betaCsv";
import { searchParam, useDebouncedValue } from "./admin/shared";

import type { ApiErrorCode } from "../api/client";
import type {
  BetaApplication,
  BetaApplicationList,
} from "@canvaschool/contracts";

type Decision = {
  kind: "approve" | "reject" | "resend" | "delete";
  item: BetaApplication;
};

type Status = BetaApplication["status"];
type Counts = BetaApplicationList["counts"];

const TABS: Array<{ status: Status | null; label: string }> = [
  { status: null, label: "전체" },
  { status: "PENDING", label: "대기" },
  { status: "APPROVED", label: "승인" },
  { status: "REJECTED", label: "거절" },
];

/** Administrators decide open-beta applications; approval creates the account. */
export const AdminBetaApplicationsPage = ({
  initialStatus = null,
}: {
  initialStatus?: Status | null;
}) => {
  const auth = useAuth();
  const { api } = auth;
  const [items, setItems] = useState<BetaApplication[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [status, setStatus] = useState<Status | null>(initialStatus);
  const [search, setSearch] = useState("");
  const q = searchParam(useDebouncedValue(search));
  const filterRef = useRef({ status, q });
  filterRef.current = { status, q };
  /** Only the newest request may write the list. */
  const requestRef = useRef(0);

  const load = useCallback(
    async (clearError = false) => {
      const ticket = ++requestRef.current;
      const filter = filterRef.current;
      if (clearError) {
        setError(null);
      }
      try {
        const result = await api.listBetaApplications({
          status: filter.status ?? undefined,
          q: filter.q,
        });
        if (ticket === requestRef.current) {
          setItems(result.items);
          setNextCursor(result.nextCursor);
          setCounts(result.counts);
        }
      } catch (failure) {
        if (ticket === requestRef.current) {
          setError(auth.handleError(failure));
        }
      }
    },
    [api, auth],
  );

  useEffect(() => {
    void load(true);
    // Reloading per filter; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, status, q]);

  const loadMore = async () => {
    if (!nextCursor || busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    // A filter change takes a newer ticket, which voids this request.
    const ticket = ++requestRef.current;
    try {
      const result = await api.listBetaApplications({
        status: status ?? undefined,
        q,
        cursor: nextCursor,
      });
      if (ticket === requestRef.current) {
        setItems((current) => [...(current ?? []), ...result.items]);
        setNextCursor(result.nextCursor);
        setCounts(result.counts);
      }
    } catch (failure) {
      if (ticket === requestRef.current) {
        setError(auth.handleError(failure));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const all: BetaApplication[] = [];
      let cursor: string | undefined;
      do {
        const result = await api.listBetaApplications({
          status: status ?? undefined,
          q,
          cursor,
          limit: 100,
        });
        all.push(...result.items);
        cursor = result.nextCursor ?? undefined;
      } while (cursor);
      downloadCsv(buildBetaCsv(all), betaCsvFileName());
    } catch (failure) {
      setError(auth.handleError(failure));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  /** One decision at a time, so a double click never repeats it. */
  const run = async (action: () => Promise<unknown>) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
      setDecision(null);
      await load();
    } catch (failure) {
      setError(auth.handleError(failure));
      setDecision(null);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const tabCount = (tab: Status | null) =>
    !counts
      ? null
      : tab === "PENDING"
      ? counts.pending
      : tab === "APPROVED"
      ? counts.approved
      : tab === "REJECTED"
      ? counts.rejected
      : counts.pending + counts.approved + counts.rejected;

  const confirm = () => {
    if (!decision) {
      return;
    }
    const { kind, item } = decision;
    void run(() =>
      kind === "approve"
        ? api.approveBetaApplication(item.id)
        : kind === "reject"
        ? api.rejectBetaApplication(item.id)
        : kind === "delete"
        ? api.deleteBetaApplication(item.id)
        : api.resendBetaCredentials(item.id),
    );
  };

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>베타 신청</h1>
        </div>
        <button type="button" onClick={() => void exportCsv()} disabled={busy}>
          CSV 내려받기
        </button>
      </div>
      <div className="canvaschool-tabs" role="tablist" aria-label="상태">
        {TABS.map((tab) => {
          const total = tabCount(tab.status);
          return (
            <button
              key={tab.label}
              type="button"
              role="tab"
              aria-selected={status === tab.status}
              className="canvaschool-tab"
              onClick={() => setStatus(tab.status)}
            >
              {tab.label}
              {total !== null && ` ${total}`}
            </button>
          );
        })}
      </div>
      <div className="canvaschool-inline-form">
        <label htmlFor="canvaschool-beta-search">신청 검색</label>
        <input
          id="canvaschool-beta-search"
          type="search"
          value={search}
          placeholder="이름, 학교, 이메일"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {error && <ErrorAlert code={error} />}
      {items && items.length === 0 && (
        <p className="canvaschool-hint">조건에 맞는 신청이 없습니다.</p>
      )}
      {items && items.length > 0 && (
        <div className="canvaschool-table-wrap">
          <table className="canvaschool-table">
            <thead>
              <tr>
                <th scope="col">신청일</th>
                <th scope="col">이름</th>
                <th scope="col">학교</th>
                <th scope="col">이메일</th>
                <th scope="col">남긴 말</th>
                <th scope="col">상태</th>
                <th scope="col">계정</th>
                <th scope="col">관리</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.createdAt.slice(0, 10)}</td>
                  <td>{item.name}</td>
                  <td>{item.schoolName}</td>
                  <td>{item.email}</td>
                  <td>{item.message}</td>
                  <td>
                    <span
                      className={`canvaschool-status ${
                        item.status === "APPROVED"
                          ? "canvaschool-status--on"
                          : item.status === "PENDING"
                          ? "canvaschool-status--wait"
                          : "canvaschool-status--off"
                      }`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td>
                    {item.teacherLoginId && (
                      <span className="canvaschool-prefix">
                        {item.teacherLoginId}
                      </span>
                    )}
                    {item.status === "APPROVED" &&
                      item.credentialsSentAt === null && (
                        <span className="canvaschool-status canvaschool-status--bad">
                          안내 메일 미발송
                        </span>
                      )}
                  </td>
                  <td>
                    <div className="canvaschool-actions">
                      {item.status === "PENDING" && (
                        <>
                          <button
                            type="button"
                            className="canvaschool-btn-primary"
                            aria-label={`${item.name} 승인`}
                            onClick={() =>
                              setDecision({ kind: "approve", item })
                            }
                          >
                            승인
                          </button>
                          <button
                            type="button"
                            className="canvaschool-btn-danger"
                            aria-label={`${item.name} 거절`}
                            onClick={() =>
                              setDecision({ kind: "reject", item })
                            }
                          >
                            거절
                          </button>
                        </>
                      )}
                      {item.status !== "APPROVED" && (
                        <button
                          type="button"
                          className="canvaschool-btn-danger"
                          aria-label={`${item.name} 삭제`}
                          onClick={() => setDecision({ kind: "delete", item })}
                        >
                          삭제
                        </button>
                      )}
                      {item.status === "APPROVED" && (
                        <button
                          type="button"
                          className="canvaschool-btn-danger"
                          aria-label={`${item.name} 안내 메일 다시 보내기`}
                          onClick={() => setDecision({ kind: "resend", item })}
                        >
                          안내 메일 다시 보내기
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {nextCursor && (
        <button type="button" onClick={() => void loadMore()} disabled={busy}>
          더 보기
        </button>
      )}
      {decision && (
        <Modal
          title={
            decision.kind === "approve"
              ? "신청 승인"
              : decision.kind === "reject"
              ? "신청 거절"
              : decision.kind === "delete"
              ? "신청 삭제"
              : "안내 메일 다시 보내기"
          }
          primaryLabel={
            decision.kind === "approve"
              ? "승인"
              : decision.kind === "reject"
              ? "거절"
              : decision.kind === "delete"
              ? "삭제"
              : "다시 보내기"
          }
          danger={decision.kind === "reject" || decision.kind === "delete"}
          pending={busy}
          onCancel={() => setDecision(null)}
          onPrimary={confirm}
        >
          <div className="canvaschool-modal-info-box">
            {decision.kind === "approve" && (
              <p>
                교사 계정을 만들고 아이디와 임시 비밀번호를{" "}
                <strong>{decision.item.email}</strong>로 보냅니다.
              </p>
            )}
            {decision.kind === "reject" && (
              <p>
                <strong>{decision.item.name}</strong> 님의 신청을 거절합니다.
                안내 메일은 가지 않습니다.
              </p>
            )}
            {decision.kind === "delete" && (
              <p>
                <strong>{decision.item.name}</strong> 님의 신청을 삭제합니다.
                되돌릴 수 없습니다.
              </p>
            )}
            {decision.kind === "resend" && (
              <p>
                임시 비밀번호를 새로 만들어 다시 보냅니다. 이미 비밀번호를 바꾼
                계정도 새 임시 비밀번호로 바뀝니다.
              </p>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
};
